import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Alert,
	Spinner,
	Badge,
	Progress,
	Nav,
	NavItem,
	NavLink,
	TabContent,
	TabPane,
	Input
} from "reactstrap";
import {
	FaCheckCircle,
	FaExclamationTriangle,
	FaInfoCircle,
	FaSync,
	FaChartBar,
	FaBalanceScale,
	FaLightbulb,
	FaRobot,
	FaUserCheck
} from "react-icons/fa";
import {
	analyzeAPI,
	fixPushAPI,
	AnalyzeResponse,
	CollectionName
} from "../../services/aiAnalysisService";
import ModelSelector, { AIModel } from "../chat/ModelSelector";
import { error as showError, info as showSuccess } from "../../utils/notification";
import userService from "../../services/userService";

interface AIAnalysisResultsModalProps {
	isOpen: boolean;
	toggle: () => void;
	collectionName: CollectionName;
	collectionLabel: string;
	onAnalysisComplete?: () => void;
}

const AIAnalysisResultsModal: React.FC<AIAnalysisResultsModalProps> = ({
	isOpen,
	toggle,
	collectionName,
	collectionLabel,
	onAnalysisComplete
}) => {
	const dispatch = useDispatch();
	const [isAnalyzing, setIsAnalyzing] = useState(false);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [isApplying, setIsApplying] = useState(false);
	const [analysisResult, setAnalysisResult] = useState<AnalyzeResponse | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [applySuccess, setApplySuccess] = useState(false);
	const [activeTab, setActiveTab] = useState("summary");
	const [selectedSuggestions, setSelectedSuggestions] = useState<Set<string>>(new Set());
	const [currentOffset, setCurrentOffset] = useState(0);
	const [hasMore, setHasMore] = useState(false);
	const [selectedModel, setSelectedModel] = useState<AIModel>("claude");

	// Ref to track if component is mounted - prevents state updates after unmount
	const isMountedRef = useRef(true);
	const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Set up mounted ref and cleanup on unmount
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			// Clear any pending timeouts
			if (closeTimeoutRef.current) {
				clearTimeout(closeTimeoutRef.current);
			}
		};
	}, []);

	// Auto-run analysis when modal opens
	useEffect(() => {
		if (isOpen && !analysisResult && !isAnalyzing && !error) {
			handleRunAnalysis();
		}
		// Reset state when modal closes
		if (!isOpen) {
			setAnalysisResult(null);
			setError(null);
			setApplySuccess(false);
			setIsAnalyzing(false);
			setIsLoadingMore(false);
			setIsApplying(false);
			setActiveTab("summary");
			setSelectedSuggestions(new Set());
			setCurrentOffset(0);
			setHasMore(false);
			setSelectedModel("claude");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen]);

	const handleRunAnalysis = async () => {
		// Check if component is still mounted before setting state
		if (!isMountedRef.current) return;

		setIsAnalyzing(true);
		setError(null);
		setAnalysisResult(null);
		setApplySuccess(false);
		setSelectedSuggestions(new Set());

		try {
			const result = await analyzeAPI({
				collection_name: collectionName,
				limit: 10,
				model: selectedModel
			});

			// Check if component is still mounted before setting state
			if (!isMountedRef.current) return;

			if (!result.success) {
				throw new Error(result.error || "Failed to analyze data");
			}

			// Atomic Sync: Refresh credits
			dispatch(userService.getUserCredits() as any);

			setAnalysisResult(result);
			setCurrentOffset(10);
			// Check if there might be more items
			// If we got 10 items, there might be more (we don't know total until we check)
			// For now, assume there might be more if we got exactly 10 items
			const itemsProcessed = result.summary?.total_records || result.items_analyzed || 10;
			setHasMore(itemsProcessed >= 10);

			// Auto-select all suggestions by default
			if (result.push_suggestions?.suggestions) {
				const allIds = result.push_suggestions.suggestions.map((s) => `${s.record_id}-${s.field}`);
				setSelectedSuggestions(new Set(allIds));
			}
		} catch (err) {
			// Check if component is still mounted before setting state
			if (!isMountedRef.current) return;

			console.error("Error running analysis:", err);
			const errorMessage =
				err instanceof Error ? err.message : "Failed to analyze data. Please try again.";
			setError(errorMessage);
			showError({
				title: "Analysis Failed",
				description: errorMessage
			});
		} finally {
			// Only update state if component is still mounted
			if (isMountedRef.current) {
				setIsAnalyzing(false);
			}
		}
	};

	const handleLoadNext = async () => {
		if (!analysisResult || isLoadingMore || !isMountedRef.current) return;

		setIsLoadingMore(true);
		setError(null);

		try {
			const result = await analyzeAPI({
				collection_name: collectionName,
				limit: 10,
				offset: currentOffset,
				model: selectedModel
			});

			// Check if component is still mounted before setting state
			if (!isMountedRef.current) return;

			if (!result.success) {
				throw new Error(result.error || "Failed to load more data");
			}

			// Merge results
			const mergedResult: AnalyzeResponse = {
				...analysisResult,
				// Merge summary - update totals
				summary: result.summary
					? {
						...result.summary,
						total_records:
							(analysisResult.summary?.total_records || 0) + (result.summary?.total_records || 0),
						complete_records:
							(analysisResult.summary?.complete_records || 0) +
							(result.summary?.complete_records || 0),
						incomplete_records:
							(analysisResult.summary?.incomplete_records || 0) +
							(result.summary?.incomplete_records || 0),
						// Merge categories
						categories: [
							...(analysisResult.summary?.categories || []),
							...(result.summary?.categories || [])
						],
						// Merge critical issues
						critical_issues: Array.from(
							new Set([
								...(analysisResult.summary?.critical_issues || []),
								...(result.summary?.critical_issues || [])
							])
						)
					}
					: analysisResult.summary,
				// Merge compare data
				compare: {
					discrepancies: [
						...(analysisResult.compare?.discrepancies || []),
						...(result.compare?.discrepancies || [])
					],
					confidence_scores: [
						...(analysisResult.compare?.confidence_scores || []),
						...(result.compare?.confidence_scores || [])
					]
				},
				// Merge push suggestions
				push_suggestions: {
					suggestions: [
						...(analysisResult.push_suggestions?.suggestions || []),
						...(result.push_suggestions?.suggestions || [])
					],
					auto_fills: [
						...(analysisResult.push_suggestions?.auto_fills || []),
						...(result.push_suggestions?.auto_fills || [])
					],
					manual_review: [
						...(analysisResult.push_suggestions?.manual_review || []),
						...(result.push_suggestions?.manual_review || [])
					]
				}
			};

			setAnalysisResult(mergedResult);
			const newOffset = currentOffset + 10;
			setCurrentOffset(newOffset);

			// Check if there are more items
			// If we got 10 items in this batch, there might be more
			const itemsInBatch = result.summary?.total_records || result.items_analyzed || 0;
			setHasMore(itemsInBatch >= 10);

			// Auto-select new suggestions
			if (result.push_suggestions?.suggestions) {
				const newIds = result.push_suggestions.suggestions.map((s) => `${s.record_id}-${s.field}`);
				const updatedSet = new Set(selectedSuggestions);
				newIds.forEach((id) => updatedSet.add(id));
				setSelectedSuggestions(updatedSet);
			}
		} catch (err) {
			// Check if component is still mounted before setting state
			if (!isMountedRef.current) return;

			console.error("Error loading more:", err);
			const errorMessage =
				err instanceof Error ? err.message : "Failed to load more data. Please try again.";
			setError(errorMessage);
			showError({
				title: "Load Failed",
				description: errorMessage
			});
		} finally {
			// Only update state if component is still mounted
			if (isMountedRef.current) {
				setIsLoadingMore(false);
			}
		}
	};

	const handleApplyChanges = async () => {
		if (!analysisResult?.analysis_id || !isMountedRef.current) {
			if (isMountedRef.current) {
				showError({
					title: "Error",
					description: "No analysis ID available. Please run analysis again."
				});
			}
			return;
		}

		setIsApplying(true);
		setError(null);
		setApplySuccess(false);

		try {
			const result = await fixPushAPI({
				analysis_id: analysisResult.analysis_id
			});

			// Check if component is still mounted before setting state
			if (!isMountedRef.current) return;

			if (!result.success) {
				throw new Error(result.error || "Failed to apply changes");
			}

			// Atomic Sync: Refresh credits
			dispatch(userService.getUserCredits() as any);

			setApplySuccess(true);
			showSuccess({
				title: "Success",
				description: result.message || `Successfully updated ${result.items_updated || 0} item(s)`
			});

			// Call the callback if provided
			if (onAnalysisComplete) {
				onAnalysisComplete();
			}

			// Close modal after a short delay - with cleanup
			closeTimeoutRef.current = setTimeout(() => {
				if (isMountedRef.current) {
					handleClose();
				}
			}, 2000);
		} catch (err) {
			// Check if component is still mounted before setting state
			if (!isMountedRef.current) return;

			console.error("Error applying changes:", err);
			const errorMessage =
				err instanceof Error ? err.message : "Failed to apply changes. Please try again.";
			setError(errorMessage);
			showError({
				title: "Apply Failed",
				description: errorMessage
			});
		} finally {
			// Only update state if component is still mounted
			if (isMountedRef.current) {
				setIsApplying(false);
			}
		}
	};

	const handleClose = () => {
		setAnalysisResult(null);
		setError(null);
		setApplySuccess(false);
		setIsAnalyzing(false);
		setIsApplying(false);
		setActiveTab("summary");
		setSelectedSuggestions(new Set());
		toggle();
	};

	const formatValue = (value: any): string => {
		if (value === null || value === undefined) {
			return "N/A";
		}
		if (typeof value === "object") {
			return JSON.stringify(value, null, 2);
		}
		return String(value);
	};

	const getCompletenessColor = (score: number): string => {
		if (score >= 80) return "success";
		if (score >= 50) return "warning";
		return "danger";
	};

	const getPriorityColor = (priority: string): string => {
		const p = priority?.toLowerCase() || "";
		if (p === "high") return "danger";
		if (p === "medium") return "warning";
		return "info";
	};

	const toggleSuggestion = (recordId: string, field: string) => {
		const id = `${recordId}-${field}`;
		const newSet = new Set(selectedSuggestions);
		if (newSet.has(id)) {
			newSet.delete(id);
		} else {
			newSet.add(id);
		}
		setSelectedSuggestions(newSet);
	};

	const summary = analysisResult?.summary;
	const compare = analysisResult?.compare;
	const pushSuggestions = analysisResult?.push_suggestions;
	const metadata = analysisResult?.metadata;

	return (
		<>
			<style>{`
                .ai-analysis-modal .modal-dialog {
                    max-width: 95%;
                    margin: 1rem auto;
                }
                @media (min-width: 576px) {
                    .ai-analysis-modal .modal-dialog {
                        max-width: 90%;
                    }
                }
                @media (min-width: 992px) {
                    .ai-analysis-modal .modal-dialog {
                        max-width: 1200px;
                    }
                }
                .ai-analysis-modal .table-responsive {
                    overflow-x: auto;
                    -webkit-overflow-scrolling: touch;
                }
                .ai-analysis-modal table {
                    min-width: 600px;
                }
                .ai-analysis-modal table td,
                .ai-analysis-modal table th {
                    word-wrap: break-word;
                    word-break: break-word;
                    white-space: normal;
                    vertical-align: top;
                    padding: 0.5rem;
                }
                .ai-analysis-modal table code {
                    word-break: break-all;
                    white-space: normal;
                }
                .ai-analysis-modal .table-sm td,
                .ai-analysis-modal .table-sm th {
                    font-size: 0.875rem;
                }
                @media (max-width: 768px) {
                    .ai-analysis-modal .nav-tabs {
                        flex-wrap: wrap;
                    }
                    .ai-analysis-modal .nav-tabs .nav-item {
                        flex: 1 1 auto;
                        min-width: 120px;
                    }
                    .ai-analysis-modal .nav-tabs .nav-link {
                        padding: 0.5rem;
                        font-size: 0.85rem;
                    }
                    .ai-analysis-modal .card-body {
                        padding: 0.75rem;
                    }
                }
            `}</style>
			<Modal isOpen={isOpen} toggle={handleClose} size="xl" className="ai-analysis-modal">
				<ModalHeader toggle={handleClose}>
					<div className="d-flex align-items-center gap-3 flex-wrap">
						<div className="d-flex align-items-center gap-2">
							<FaInfoCircle />
							<span>AI Analysis Results - {collectionLabel}</span>
						</div>
						<ModelSelector
							selectedModel={selectedModel}
							onModelSelect={(model) => setSelectedModel(model)}
							className="ms-2"
						/>
					</div>
				</ModalHeader>
				<ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
					{!analysisResult && !isAnalyzing && !error && (
						<div className="text-center py-4">
							<p className="text-muted mb-4">
								Click "Run Analysis" to analyze {collectionLabel.toLowerCase()} and get
								recommendations.
							</p>
							<Button
								color="primary"
								onClick={handleRunAnalysis}
								className="d-flex align-items-center gap-2 mx-auto"
							>
								<FaSync /> Run Analysis
							</Button>
						</div>
					)}

					{isAnalyzing && (
						<div className="text-center py-5">
							<Spinner color="primary" className="mb-3" />
							<p className="text-muted">
								Analyzing {collectionLabel.toLowerCase()}... This may take a moment.
							</p>
						</div>
					)}

					{error && !isAnalyzing && (
						<Alert color="danger" className="mb-3">
							<FaExclamationTriangle className="me-2" />
							{error}
						</Alert>
					)}

					{analysisResult && !isAnalyzing && (
						<>
							{applySuccess && (
								<Alert color="success" className="mb-3">
									<FaCheckCircle className="me-2" />
									Changes applied successfully!
								</Alert>
							)}

							{/* AI Model Used Badge */}
							{(metadata?.ai_model_used || metadata?.claude_model_used) && (
								<div className="d-flex align-items-center gap-2 mb-3">
									<FaRobot className="text-muted" />
									<small className="text-muted">Analyzed by:</small>
									<Badge color="secondary" pill>
										{metadata.ai_model_used || metadata.claude_model_used}
									</Badge>
								</div>
							)}

							{/* Summary Cards */}
							{summary && (
								<div className="row mb-4">
									<div className="col-md-3 mb-3">
										<div className="card border-0 shadow-sm">
											<div className="card-body text-center">
												<h3
													className="mb-1"
													style={{
														color: `var(--bs-${getCompletenessColor(summary.completeness_score)})`
													}}
												>
													{summary.completeness_score}%
												</h3>
												<small className="text-muted">Completeness Score</small>
											</div>
										</div>
									</div>
									<div className="col-md-3 mb-3">
										<div className="card border-0 shadow-sm">
											<div className="card-body text-center">
												<h3 className="mb-1 text-primary">{summary.total_records}</h3>
												<small className="text-muted">Total Records</small>
											</div>
										</div>
									</div>
									<div className="col-md-3 mb-3">
										<div className="card border-0 shadow-sm">
											<div className="card-body text-center">
												<h3 className="mb-1 text-success">{summary.complete_records}</h3>
												<small className="text-muted">Complete</small>
											</div>
										</div>
									</div>
									<div className="col-md-3 mb-3">
										<div className="card border-0 shadow-sm">
											<div className="card-body text-center">
												<h3 className="mb-1 text-warning">{summary.incomplete_records}</h3>
												<small className="text-muted">Incomplete</small>
											</div>
										</div>
									</div>
								</div>
							)}

							{/* Tabs */}
							<Nav tabs className="mb-3">
								<NavItem>
									<NavLink
										className={activeTab === "summary" ? "active" : ""}
										onClick={() => setActiveTab("summary")}
										style={{ cursor: "pointer" }}
									>
										<FaChartBar className="me-1" /> Summary
									</NavLink>
								</NavItem>
								<NavItem>
									<NavLink
										className={activeTab === "compare" ? "active" : ""}
										onClick={() => setActiveTab("compare")}
										style={{ cursor: "pointer" }}
									>
										<FaBalanceScale className="me-1" /> Compare
									</NavLink>
								</NavItem>
								<NavItem>
									<NavLink
										className={activeTab === "suggestions" ? "active" : ""}
										onClick={() => setActiveTab("suggestions")}
										style={{ cursor: "pointer" }}
									>
										<FaLightbulb className="me-1" /> Suggestions
										{pushSuggestions?.suggestions && (
											<Badge color="primary" className="ms-2">
												{pushSuggestions.suggestions.length}
											</Badge>
										)}
									</NavLink>
								</NavItem>
								<NavItem>
									<NavLink
										className={activeTab === "auto-fills" ? "active" : ""}
										onClick={() => setActiveTab("auto-fills")}
										style={{ cursor: "pointer" }}
									>
										<FaRobot className="me-1" /> Auto-Fills
										{pushSuggestions?.auto_fills && (
											<Badge color="info" className="ms-2">
												{pushSuggestions.auto_fills.length}
											</Badge>
										)}
									</NavLink>
								</NavItem>
								<NavItem>
									<NavLink
										className={activeTab === "manual-review" ? "active" : ""}
										onClick={() => setActiveTab("manual-review")}
										style={{ cursor: "pointer" }}
									>
										<FaUserCheck className="me-1" /> Manual Review
										{pushSuggestions?.manual_review && (
											<Badge color="warning" className="ms-2">
												{pushSuggestions.manual_review.length}
											</Badge>
										)}
									</NavLink>
								</NavItem>
							</Nav>

							<TabContent activeTab={activeTab}>
								{/* Summary Tab */}
								<TabPane tabId="summary">
									{summary && (
										<div>
											<div className="mb-4">
												<h6 className="mb-3">Overall Completeness</h6>
												<Progress
													value={summary.completeness_score}
													color={getCompletenessColor(summary.completeness_score)}
													className="mb-2"
													style={{ height: "30px" }}
												>
													{summary.completeness_score}%
												</Progress>
											</div>

											{summary.categories && summary.categories.length > 0 && (
												<div className="mb-4">
													<h6 className="mb-3">Categories Breakdown</h6>
													<div className="table-responsive" style={{ overflowX: "auto" }}>
														<table
															className="table table-sm table-hover"
															style={{ width: "100%", tableLayout: "auto" }}
														>
															<thead className="table-light">
																<tr>
																	<th style={{ minWidth: "150px" }}>Category</th>
																	<th style={{ width: "80px", minWidth: "80px" }}>Count</th>
																	<th style={{ minWidth: "150px" }}>Completeness</th>
																	<th style={{ minWidth: "200px" }}>Missing Fields</th>
																</tr>
															</thead>
															<tbody>
																{summary.categories.map((cat, idx) => (
																	<tr key={idx}>
																		<td>
																			<strong>{cat.name}</strong>
																		</td>
																		<td>{cat.count}</td>
																		<td>
																			<Progress
																				value={cat.completeness_score}
																				color={getCompletenessColor(cat.completeness_score)}
																				style={{ width: "100px", height: "20px" }}
																			>
																				{cat.completeness_score}%
																			</Progress>
																		</td>
																		<td>
																			{cat.missing_fields.map((field, fIdx) => (
																				<Badge key={fIdx} color="secondary" className="me-1">
																					{field}
																				</Badge>
																			))}
																		</td>
																	</tr>
																))}
															</tbody>
														</table>
													</div>
												</div>
											)}

											{summary.critical_issues && summary.critical_issues.length > 0 && (
												<div>
													<h6 className="mb-3">Critical Issues</h6>
													<Alert color="danger">
														<ul className="mb-0">
															{summary.critical_issues.map((issue, idx) => (
																<li key={idx}>{issue}</li>
															))}
														</ul>
													</Alert>
												</div>
											)}
										</div>
									)}
								</TabPane>

								{/* Compare Tab */}
								<TabPane tabId="compare">
									{compare && (
										<div>
											{compare.discrepancies && compare.discrepancies.length > 0 && (
												<div className="mb-4">
													<h6 className="mb-3">Data Discrepancies</h6>
													<div className="table-responsive" style={{ overflowX: "auto" }}>
														<table
															className="table table-sm table-hover"
															style={{ width: "100%", tableLayout: "auto" }}
														>
															<thead className="table-light">
																<tr>
																	<th style={{ minWidth: "120px" }}>Field</th>
																	<th style={{ minWidth: "150px" }}>Internal Value</th>
																	<th style={{ minWidth: "150px" }}>External Value</th>
																	<th style={{ minWidth: "150px" }}>Source</th>
																	<th style={{ width: "100px", minWidth: "100px" }}>Confidence</th>
																	<th style={{ width: "100px", minWidth: "100px" }}>Type</th>
																</tr>
															</thead>
															<tbody>
																{compare.discrepancies.map((disc, idx) => (
																	<tr key={idx}>
																		<td>
																			<code>{disc.field}</code>
																		</td>
																		<td className="text-muted">{disc.internal_value}</td>
																		<td className="text-success fw-bold">{disc.external_value}</td>
																		<td>
																			<small>{disc.source}</small>
																		</td>
																		<td>
																			<Badge
																				color={disc.confidence_score >= 90 ? "success" : "warning"}
																			>
																				{disc.confidence_score}%
																			</Badge>
																		</td>
																		<td>
																			<Badge color="info">{disc.conflict_type}</Badge>
																		</td>
																	</tr>
																))}
															</tbody>
														</table>
													</div>
												</div>
											)}

											{compare.confidence_scores && compare.confidence_scores.length > 0 && (
												<div>
													<h6 className="mb-3">Field Confidence Scores</h6>
													<div className="table-responsive" style={{ overflowX: "auto" }}>
														<table
															className="table table-sm table-hover"
															style={{ width: "100%", tableLayout: "auto" }}
														>
															<thead className="table-light">
																<tr>
																	<th style={{ minWidth: "120px" }}>Field</th>
																	<th style={{ minWidth: "150px" }}>Score</th>
																	<th style={{ minWidth: "200px" }}>Reason</th>
																	<th style={{ minWidth: "120px" }}>Last Updated</th>
																</tr>
															</thead>
															<tbody>
																{compare.confidence_scores.map((cs, idx) => (
																	<tr key={idx}>
																		<td>
																			<code>{cs.field}</code>
																		</td>
																		<td>
																			<Progress
																				value={cs.score}
																				color={getCompletenessColor(cs.score)}
																				style={{ width: "100px", height: "20px" }}
																			>
																				{cs.score}%
																			</Progress>
																		</td>
																		<td>
																			<small>{cs.reason}</small>
																		</td>
																		<td>
																			<small className="text-muted">{cs.last_updated}</small>
																		</td>
																	</tr>
																))}
															</tbody>
														</table>
													</div>
												</div>
											)}
										</div>
									)}
								</TabPane>

								{/* Suggestions Tab */}
								<TabPane tabId="suggestions">
									{pushSuggestions?.suggestions && pushSuggestions.suggestions.length > 0 ? (
										<div>
											<div className="d-flex justify-content-between align-items-center mb-3">
												<h6>Select suggestions to apply</h6>
												<div>
													<Button
														size="sm"
														color="link"
														onClick={() => {
															const allIds = pushSuggestions.suggestions!.map(
																(s) => `${s.record_id}-${s.field}`
															);
															setSelectedSuggestions(new Set(allIds));
														}}
													>
														Select All
													</Button>
													<Button
														size="sm"
														color="link"
														onClick={() => setSelectedSuggestions(new Set())}
													>
														Deselect All
													</Button>
												</div>
											</div>
											<div
												className="table-responsive"
												style={{ maxHeight: "400px", overflowY: "auto", overflowX: "auto" }}
											>
												<table
													className="table table-sm table-hover"
													style={{ width: "100%", tableLayout: "auto" }}
												>
													<thead className="table-light sticky-top">
														<tr>
															<th style={{ width: "50px", minWidth: "50px" }}>Select</th>
															<th style={{ minWidth: "120px" }}>Record ID</th>
															<th style={{ minWidth: "100px" }}>Field</th>
															<th style={{ minWidth: "150px" }}>Current</th>
															<th style={{ minWidth: "150px" }}>Suggested</th>
															<th style={{ minWidth: "120px" }}>Source</th>
															<th style={{ width: "100px", minWidth: "100px" }}>Confidence</th>
															<th style={{ width: "100px", minWidth: "100px" }}>Action</th>
														</tr>
													</thead>
													<tbody>
														{pushSuggestions.suggestions.map((s, idx) => {
															const id = `${s.record_id}-${s.field}`;
															const isSelected = selectedSuggestions.has(id);
															return (
																<tr key={idx} className={isSelected ? "table-primary" : ""}>
																	<td>
																		<Input
																			type="checkbox"
																			checked={isSelected}
																			onChange={() => toggleSuggestion(s.record_id, s.field)}
																		/>
																	</td>
																	<td>
																		<code style={{ fontSize: "0.85rem" }}>{s.record_id}</code>
																	</td>
																	<td>
																		<code>{s.field}</code>
																	</td>
																	<td className="text-muted" style={{ fontSize: "0.9rem" }}>
																		{formatValue(s.current_value)}
																	</td>
																	<td
																		className="text-success fw-bold"
																		style={{ fontSize: "0.9rem" }}
																	>
																		{formatValue(s.suggested_value)}
																	</td>
																	<td>
																		<small>{s.source}</small>
																	</td>
																	<td>
																		<Badge
																			color={
																				s.confidence >= 90
																					? "success"
																					: s.confidence >= 70
																						? "warning"
																						: "secondary"
																			}
																		>
																			{s.confidence}%
																		</Badge>
																	</td>
																	<td>
																		<Badge
																			color={s.action_required === "auto_fill" ? "success" : "info"}
																		>
																			{s.action_required}
																		</Badge>
																	</td>
																</tr>
															);
														})}
													</tbody>
												</table>
											</div>
											<div className="mt-3">
												<Alert color="info">
													<strong>{selectedSuggestions.size}</strong> of{" "}
													{pushSuggestions.suggestions.length} suggestions selected
												</Alert>
											</div>
										</div>
									) : (
										<Alert color="success">
											<FaCheckCircle className="me-2" />
											No suggestions available. All data looks good!
										</Alert>
									)}
								</TabPane>

								{/* Auto-Fills Tab */}
								<TabPane tabId="auto-fills">
									{pushSuggestions?.auto_fills && pushSuggestions.auto_fills.length > 0 ? (
										<div
											className="table-responsive"
											style={{ maxHeight: "400px", overflowY: "auto", overflowX: "auto" }}
										>
											<table
												className="table table-sm table-hover"
												style={{ width: "100%", tableLayout: "auto" }}
											>
												<thead className="table-light sticky-top">
													<tr>
														<th style={{ minWidth: "120px" }}>Record ID</th>
														<th style={{ minWidth: "100px" }}>Field</th>
														<th style={{ minWidth: "200px" }}>Value</th>
														<th style={{ minWidth: "150px" }}>Source</th>
													</tr>
												</thead>
												<tbody>
													{pushSuggestions.auto_fills.map((af, idx) => (
														<tr key={idx}>
															<td>
																<code style={{ fontSize: "0.85rem" }}>{af.record_id}</code>
															</td>
															<td>
																<code>{af.field}</code>
															</td>
															<td className="text-success fw-bold">{formatValue(af.value)}</td>
															<td>
																<small>{af.source}</small>
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									) : (
										<Alert color="info">No auto-fill suggestions available.</Alert>
									)}
								</TabPane>

								{/* Manual Review Tab */}
								<TabPane tabId="manual-review">
									{pushSuggestions?.manual_review && pushSuggestions.manual_review.length > 0 ? (
										<div
											className="table-responsive"
											style={{ maxHeight: "400px", overflowY: "auto", overflowX: "auto" }}
										>
											<table
												className="table table-sm table-hover"
												style={{ width: "100%", tableLayout: "auto" }}
											>
												<thead className="table-light sticky-top">
													<tr>
														<th style={{ minWidth: "120px" }}>Record ID</th>
														<th style={{ minWidth: "100px" }}>Field</th>
														<th style={{ minWidth: "250px" }}>Reason</th>
														<th style={{ width: "100px", minWidth: "100px" }}>Priority</th>
													</tr>
												</thead>
												<tbody>
													{pushSuggestions.manual_review.map((mr, idx) => (
														<tr key={idx}>
															<td>
																<code style={{ fontSize: "0.85rem" }}>{mr.record_id}</code>
															</td>
															<td>
																<code>{mr.field}</code>
															</td>
															<td>{mr.reason}</td>
															<td>
																<Badge color={getPriorityColor(mr.priority)}>{mr.priority}</Badge>
															</td>
														</tr>
													))}
												</tbody>
											</table>
										</div>
									) : (
										<Alert color="success">No items require manual review.</Alert>
									)}
								</TabPane>
							</TabContent>

							{/* Metadata */}
							{metadata && (
								<div className="mt-3 pt-3 border-top">
									<small className="text-muted">
										Processed {metadata.records_processed} records in{" "}
										{metadata.total_processing_time.toFixed(2)}s
										{metadata.claude_model_used && ` • Model: ${metadata.claude_model_used}`}
									</small>
								</div>
							)}

							{/* Load Next 10 Button */}
							{analysisResult && hasMore && !isLoadingMore && (
								<div className="mt-3 pt-3 border-top text-center">
									<Button
										color="primary"
										onClick={handleLoadNext}
										className="d-flex align-items-center gap-2 mx-auto"
									>
										<FaSync />
										Load Next 10 Items
									</Button>
									<small className="text-muted d-block mt-2">
										Currently showing {currentOffset} of {summary?.total_records || "many"} records
									</small>
								</div>
							)}

							{isLoadingMore && (
								<div className="mt-3 pt-3 border-top text-center">
									<Spinner color="primary" className="mb-2" />
									<p className="text-muted">Loading next 10 items...</p>
								</div>
							)}
						</>
					)}
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={handleClose} disabled={isApplying}>
						{applySuccess ? "Close" : "Cancel"}
					</Button>
					{analysisResult && !applySuccess && (
						<Button
							color="success"
							onClick={handleApplyChanges}
							disabled={isApplying || !analysisResult.analysis_id || selectedSuggestions.size === 0}
							className="d-flex align-items-center gap-2"
						>
							{isApplying ? (
								<>
									<Spinner size="sm" />
									Applying {selectedSuggestions.size} changes...
								</>
							) : (
								<>
									<FaCheckCircle />
									Apply {selectedSuggestions.size} Selected Changes
								</>
							)}
						</Button>
					)}
					{!analysisResult && !isAnalyzing && (
						<Button
							color="primary"
							onClick={handleRunAnalysis}
							className="d-flex align-items-center gap-2"
						>
							<FaSync />
							Run Analysis
						</Button>
					)}
				</ModalFooter>
			</Modal>
		</>
	);
};

export default AIAnalysisResultsModal;
